// SPDX-License-Identifier: AGPL-3.0-or-later

import i18n from '@app/app/I18n';
import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import {isDesktop} from '@app/features/ui/utils/NativeUtils';
import type {SettingsTab} from '@app/features/user/components/settings_utils/SettingsConstants';
import {
	getSearchableItemsFromRegistry,
	type SearchableSettingItem,
	type UserSettingsTabType,
} from '@app/features/user/components/settings_utils/SettingsSectionRegistry';
import Users from '@app/features/user/state/Users';
import type {I18n, MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

interface SearchableSettingDescriptor {
	id: string;
	tabType: UserSettingsTabType;
	sectionId?: string;
	label: MessageDescriptor;
	keywords: Array<MessageDescriptor>;
	description?: MessageDescriptor;
	isVisible?: () => boolean;
}
export interface SettingsSearchResult {
	tab: SettingsTab;
	matchedItems: Array<SearchableSettingItem>;
	score: number;
}
function shouldShowClaimedAccountSettings(): boolean {
	return Users.getCurrentUser()?.isClaimed() ?? true;
}
function isSearchableDescriptorVisible(item: SearchableSettingDescriptor): boolean {
	return !item.isVisible || item.isVisible();
}
function translateSearchableDescriptor(item: SearchableSettingDescriptor, activeI18n: I18n): SearchableSettingItem {
	const {id, tabType, sectionId, label, keywords, description} = item;
	return {
		id,
		tabType,
		sectionId,
		label: activeI18n._(label),
		keywords: keywords.map((keyword) => activeI18n._(keyword)),
		description: description ? activeI18n._(description) : undefined,
	};
}
const ADDITIONAL_SEARCHABLE_ITEMS: Array<SearchableSettingDescriptor> = [
	{
		id: 'first-click-pass-through',
		tabType: 'advanced',
		label: msg`First Click Pass-Through When Unfocused`,
		keywords: [
			msg`first click`,
			msg`click through`,
			msg`unfocused`,
			msg`window focus`,
			msg`focus click`,
			msg`activation click`,
		],
		description: msg`Let the click that focuses ${PRODUCT_NAME} also activate the control under the pointer.`,
	},
	{
		id: 'profile-avatar',
		tabType: 'my_profile',
		label: msg`Avatar`,
		keywords: [msg`avatar`, msg`profile picture`, msg`photo`, msg`image`, msg`pfp`, msg`picture`],
		description: msg`Change your profile picture`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'profile-banner',
		tabType: 'my_profile',
		label: msg`Banner`,
		keywords: [msg`banner`, msg`cover`, msg`header`, msg`background`],
		description: msg`Customize your profile banner`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'profile-username',
		tabType: 'my_profile',
		label: msg`Username`,
		keywords: [msg`username`, msg`name`, msg`display name`, msg`user`, msg`handle`, msg`tag`],
		description: msg`Change your username`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'profile-display-name',
		tabType: 'my_profile',
		label: msg`Display Name`,
		keywords: [msg`display name`, msg`global name`, msg`profile name`, msg`name`],
		description: msg`Change the name shown on your global profile`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'profile-type',
		tabType: 'my_profile',
		label: msg`Profile Type`,
		keywords: [msg`profile type`, msg`global profile`, msg`community profile`, msg`per-community profile`],
		description: msg`Switch between your global profile and per-community profiles`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'profile-community-nickname',
		tabType: 'my_profile',
		label: msg`Community Nickname`,
		keywords: [msg`nickname`, msg`community nickname`, msg`server nickname`, msg`per-community profile`],
		description: msg`Change your nickname for a community profile`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'profile-bio',
		tabType: 'my_profile',
		label: msg`Bio`,
		keywords: [msg`bio`, msg`about me`, msg`description`, msg`biography`, msg`about`],
		description: msg`Edit your profile bio`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'profile-accent-color',
		tabType: 'my_profile',
		label: msg`Accent Color`,
		keywords: [msg`accent`, msg`color`, msg`theme color`, msg`profile color`],
		description: msg`Choose your profile accent color`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'profile-pronouns',
		tabType: 'my_profile',
		label: msg`Pronouns`,
		keywords: [msg`pronouns`, msg`identity`, msg`he`, msg`she`, msg`they`],
		description: msg`Set your pronouns`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'profile-badge',
		tabType: 'my_profile',
		label: msg`Badge`,
		keywords: [msg`badge`, msg`premium badge`, msg`plutonium`],
		description: msg`Configure your profile badge`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'account-email',
		tabType: 'account_security',
		sectionId: 'account',
		label: msg`Email`,
		keywords: [msg`email`, msg`mail`, msg`address`, msg`contact`],
		description: msg`Change your email address`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'account-password',
		tabType: 'account_security',
		sectionId: 'security',
		label: msg`Password`,
		keywords: [msg`password`, msg`credentials`, msg`login`, msg`security`, msg`change password`],
		description: msg`Change your password`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'account-2fa',
		tabType: 'account_security',
		sectionId: 'security',
		label: msg`Two-Factor Authentication`,
		keywords: [
			msg`2fa`,
			msg`two factor`,
			msg`mfa`,
			msg`authentication`,
			msg`authenticator`,
			msg`totp`,
			msg`otp`,
			msg`security`,
		],
		description: msg`Enable two-factor authentication`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'account-backup-codes',
		tabType: 'account_security',
		sectionId: 'security',
		label: msg`Backup Codes`,
		keywords: [msg`backup codes`, msg`recovery`, msg`codes`, msg`2fa backup`],
		description: msg`View or regenerate backup codes`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'account-passkeys',
		tabType: 'account_security',
		sectionId: 'security',
		label: msg`Passkeys`,
		keywords: [
			msg`passkey`,
			msg`passkeys`,
			msg`webauthn`,
			msg`passwordless`,
			msg`face id`,
			msg`touch id`,
			msg`biometric`,
			msg`biometrics`,
			msg`fingerprint`,
			msg`security key`,
			msg`yubikey`,
			msg`add passkey`,
		],
		description: msg`Use passkeys for passwordless sign-in and two-factor authentication`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'account-disable',
		tabType: 'account_security',
		sectionId: 'danger_zone',
		label: msg`Disable Account`,
		keywords: [msg`disable`, msg`deactivate`, msg`suspend`],
		description: msg`Temporarily disable your account`,
		isVisible: shouldShowClaimedAccountSettings,
	},
	{
		id: 'account-delete',
		tabType: 'account_security',
		sectionId: 'danger_zone',
		label: msg`Delete Account`,
		keywords: [msg`delete`, msg`remove`, msg`close account`, msg`deactivate`],
		description: msg`Delete your account`,
	},
	{
		id: 'language-locale',
		tabType: 'language',
		label: msg`Language`,
		keywords: [msg`language`, msg`locale`, msg`translation`, msg`localization`],
		description: msg`Choose your language`,
	},
	{
		id: 'language-time-format',
		tabType: 'language',
		label: msg`Time Format`,
		keywords: [
			msg`time format`,
			msg`time`,
			msg`12 hour`,
			msg`24 hour`,
			msg`12-hour`,
			msg`24-hour`,
			msg`am pm`,
			msg`am/pm`,
			msg`clock`,
			msg`military time`,
			msg`system time format`,
			msg`browser time format`,
			msg`locale time format`,
		],
		description: msg`Choose how times are displayed throughout the app`,
	},
	{
		id: 'language-spellcheck',
		tabType: 'language',
		label: msg`Spellcheck`,
		keywords: [
			msg`spellcheck`,
			msg`spell check`,
			msg`spelling`,
			msg`dictionary`,
			msg`dictionaries`,
			msg`hunspell`,
			msg`personal dictionary`,
			msg`auto-detect language`,
			msg`misspelled words`,
		],
		description: msg`Configure spellcheck, dictionaries, and personal words`,
	},
	{
		id: 'keybinds-shortcuts',
		tabType: 'keybinds',
		label: msg`Keyboard Shortcuts`,
		keywords: [
			msg`keybinds`,
			msg`shortcuts`,
			msg`hotkeys`,
			msg`keyboard`,
			msg`keys`,
			msg`hotkey`,
			msg`shortcut`,
			msg`bind`,
			msg`bindings`,
			msg`quick switcher`,
			msg`mark read`,
			msg`mark as read`,
			msg`mark channel as read`,
			msg`bookmark`,
			msg`bookmark message`,
			msg`pin`,
			msg`pins popout`,
			msg`inbox`,
			msg`inbox popout`,
			msg`zoom in`,
			msg`zoom out`,
			msg`reload`,
			msg`navigate`,
		],
		description: msg`Configure keyboard shortcuts`,
	},
	{
		id: 'keybinds-custom',
		tabType: 'keybinds',
		label: msg`Custom Keybinds`,
		keywords: [
			msg`custom keybinds`,
			msg`custom shortcuts`,
			msg`add a keybind`,
			msg`reset to defaults`,
			msg`disable built-in keybinds`,
			msg`sync keybinds`,
			msg`keybind conflicts`,
		],
		description: msg`Add custom keybinds, disable built-in shortcuts, and sync keybind preferences`,
	},
	{
		id: 'keybinds-defaults',
		tabType: 'keybinds',
		label: msg`Default Keybinds`,
		keywords: [
			msg`default keybinds`,
			msg`built-in shortcuts`,
			msg`messages shortcuts`,
			msg`navigation shortcuts`,
			msg`chat shortcuts`,
			msg`voice and video shortcuts`,
		],
		description: msg`Review the built-in keyboard shortcuts by section`,
	},
	{
		id: 'keybinds-ptt',
		tabType: 'keybinds',
		label: msg`Push to Talk Key`,
		keywords: [msg`push to talk`, msg`ptt`, msg`keybind`, msg`key`],
		description: msg`Set push to talk key`,
	},
	{
		id: 'keybinds-mute',
		tabType: 'keybinds',
		label: msg`Mute Keybind`,
		keywords: [msg`mute`, msg`keybind`, msg`toggle mute`],
		description: msg`Set mute keybind`,
	},
	{
		id: 'keybinds-deafen',
		tabType: 'keybinds',
		label: msg`Deafen Keybind`,
		keywords: [msg`deafen`, msg`keybind`, msg`toggle deafen`],
		description: msg`Set deafen keybind`,
	},
	{
		id: 'accessibility-confirm-calls',
		tabType: 'accessibility',
		sectionId: 'keyboard',
		label: msg`Confirm before starting calls`,
		keywords: [
			msg`call confirmation`,
			msg`confirm calls`,
			msg`start call`,
			msg`voice call`,
			msg`video call`,
			msg`ring`,
			msg`silent call`,
			msg`shift`,
		],
		description: msg`Choose whether starting a call asks for confirmation`,
	},
	{
		id: 'appearance-confirm-voice-channel-join',
		tabType: 'appearance',
		sectionId: 'interface',
		label: msg`Confirm before joining voice channels`,
		keywords: [
			msg`voice channel confirmation`,
			msg`confirm voice join`,
			msg`join voice`,
			msg`voice channel`,
			msg`community voice`,
			msg`two clicks`,
			msg`2 clicks`,
			msg`vc`,
		],
		description: msg`Choose whether joining a community voice channel asks for confirmation`,
	},
	{
		id: 'advanced-developer-mode',
		tabType: 'advanced',
		label: msg`Developer Mode`,
		keywords: [msg`developer`, msg`developer mode`, msg`dev`, msg`debug`, msg`copy id`],
		description: msg`Enable developer mode`,
	},
	{
		id: 'advanced-stay-interactive-unfocused',
		tabType: 'advanced',
		label: msg`Stay Fully Interactive When Unfocused`,
		keywords: [
			msg`unfocused`,
			msg`window focus`,
			msg`animations while unfocused`,
			msg`gif playback`,
			msg`hover effects`,
			msg`tooltips`,
			msg`power saving`,
		],
		description: msg`Keep animations, GIF playback, hover effects, and tooltips running while another app has focus`,
	},
	{
		id: 'advanced-hardware-acceleration',
		tabType: 'advanced',
		label: msg`Hardware Acceleration`,
		keywords: [msg`hardware`, msg`acceleration`, msg`gpu`, msg`graphics`, msg`performance`],
		description: msg`Toggle hardware acceleration`,
		isVisible: isDesktop,
	},
	{
		id: 'advanced-screen-sharing-codec',
		tabType: 'advanced',
		label: msg`Screen Sharing Codec`,
		keywords: [
			msg`codec`,
			msg`screen sharing`,
			msg`screen share`,
			msg`video codec`,
			msg`av1`,
			msg`vp8`,
			msg`vp9`,
			msg`h264`,
			msg`livekit`,
			msg`go live`,
		],
		description: msg`Choose the video codec for screen sharing`,
	},
	{
		id: 'advanced-autostart',
		tabType: 'advanced',
		label: msg`Launch ${PRODUCT_NAME} at Login`,
		keywords: [
			msg`autostart`,
			msg`auto start`,
			msg`launch at login`,
			msg`startup`,
			msg`desktop startup`,
			msg`run at login`,
			msg`start with computer`,
			msg`boot`,
		],
		description: msg`Run ${PRODUCT_NAME} automatically when your computer starts`,
		isVisible: isDesktop,
	},
	{
		id: 'advanced-remember-window-state',
		tabType: 'advanced',
		label: msg`Remember Size & Position`,
		keywords: [
			msg`window`,
			msg`window state`,
			msg`window size`,
			msg`window position`,
			msg`remember window`,
			msg`desktop window`,
			msg`size`,
			msg`position`,
			msg`bounds`,
		],
		description: msg`Keep window dimensions and placement between reloads`,
		isVisible: isDesktop,
	},
	{
		id: 'advanced-native-title-bar',
		tabType: 'advanced',
		label: msg`Use Native Title Bar`,
		keywords: [
			msg`title bar`,
			msg`titlebar`,
			msg`native title bar`,
			msg`window chrome`,
			msg`window decorations`,
			msg`frame`,
			msg`custom title bar`,
			msg`desktop window`,
		],
		description: msg`Use the operating system's window chrome`,
		isVisible: isDesktop,
	},
	{
		id: 'advanced-tray-icon',
		tabType: 'advanced',
		label: msg`Show Tray Icon`,
		keywords: [
			msg`tray`,
			msg`tray icon`,
			msg`system tray`,
			msg`menu bar`,
			msg`status icon`,
			msg`appindicator`,
			msg`statusnotifier`,
			msg`notification area`,
			msg`desktop window`,
		],
		description: msg`Show a ${PRODUCT_NAME} icon in the system tray or menu bar`,
		isVisible: isDesktop,
	},
	{
		id: 'advanced-minimize-to-tray',
		tabType: 'advanced',
		label: msg`Minimize to Tray`,
		keywords: [
			msg`minimize`,
			msg`minimise`,
			msg`tray`,
			msg`minimize to tray`,
			msg`hide window`,
			msg`background`,
			msg`desktop window`,
		],
		description: msg`Hide the window when minimized and reopen from the tray`,
		isVisible: isDesktop,
	},
	{
		id: 'advanced-close-to-tray',
		tabType: 'advanced',
		label: msg`Close to Tray`,
		keywords: [
			msg`close`,
			msg`tray`,
			msg`close to tray`,
			msg`background`,
			msg`keep running`,
			msg`exit`,
			msg`quit`,
			msg`desktop window`,
		],
		description: msg`Keep ${PRODUCT_NAME} running when the window is closed`,
		isVisible: isDesktop,
	},
	{
		id: 'devices-sessions',
		tabType: 'devices',
		label: msg`My Devices`,
		keywords: [
			msg`devices`,
			msg`sessions`,
			msg`login`,
			msg`active`,
			msg`logged in`,
			msg`current device`,
			msg`other devices`,
			msg`linked devices`,
		],
		description: msg`View devices that are logged into your account`,
	},
	{
		id: 'devices-logout-all',
		tabType: 'devices',
		label: msg`Log Out All Other Devices`,
		keywords: [msg`logout`, msg`log out`, msg`devices`, msg`sessions`, msg`sign out`, msg`revoke device`],
		description: msg`Log out other devices from your account`,
	},
	{
		id: 'blocked-users',
		tabType: 'blocked_users',
		label: msg`Blocked Users`,
		keywords: [msg`blocked`, msg`block`, msg`users`, msg`unblock`],
		description: msg`Manage blocked users`,
	},
	{
		id: 'authorized-apps',
		tabType: 'authorized_apps',
		label: msg`Authorized Apps`,
		keywords: [msg`apps`, msg`authorized`, msg`oauth`, msg`permissions`, msg`third party`, msg`integrations`],
		description: msg`Manage authorized applications`,
	},
	{
		id: 'plutonium-subscription',
		tabType: 'plutonium',
		label: msg`Subscription`,
		keywords: [
			msg`plutonium`,
			msg`premium`,
			msg`subscription`,
			msg`upgrade`,
			msg`plan`,
			msg`billing`,
			msg`cancel subscription`,
			msg`reactivate subscription`,
			msg`customer portal`,
		],
		description: msg`Manage your subscription`,
	},
	{
		id: 'plutonium-pricing',
		tabType: 'plutonium',
		label: msg`Pricing`,
		keywords: [msg`pricing`, msg`monthly`, msg`yearly`, msg`annual`, msg`currency`, msg`checkout`, msg`purchase`],
		description: msg`Choose a subscription plan or gift option`,
	},
	{
		id: 'plutonium-gifts',
		tabType: 'plutonium',
		label: msg`Gifts`,
		keywords: [msg`gifts`, msg`gift`, msg`gift subscription`, msg`1 month gift`, msg`1 year gift`, msg`codes`],
		description: msg`Purchase premium gifts for someone else`,
	},
	{
		id: 'plutonium-purchase-history',
		tabType: 'plutonium',
		label: msg`Purchase History`,
		keywords: [msg`purchase history`, msg`billing history`, msg`invoice`, msg`receipt`, msg`refund`],
		description: msg`Review past purchases and refund options`,
	},
	{
		id: 'plutonium-perks',
		tabType: 'plutonium',
		label: msg`Free vs Premium`,
		keywords: [msg`perks`, msg`benefits`, msg`features`, msg`premium`, msg`comparison`, msg`free vs premium`],
		description: msg`View premium perks`,
	},
	{
		id: 'gift-inventory',
		tabType: 'gift_inventory',
		label: msg`Redeem a Gift`,
		keywords: [msg`gifts`, msg`inventory`, msg`codes`, msg`redeem`, msg`gift codes`, msg`gift url`],
		description: msg`Redeem a gift code for your account`,
	},
	{
		id: 'gift-purchased',
		tabType: 'gift_inventory',
		label: msg`Your Purchased Gifts`,
		keywords: [
			msg`purchased gifts`,
			msg`gift inventory`,
			msg`share gift`,
			msg`copy gift url`,
			msg`redeem for yourself`,
		],
		description: msg`Manage purchased gift codes and gift URLs`,
	},
	{
		id: 'expression-packs',
		tabType: 'expression_packs',
		label: msg`Expression Packs`,
		keywords: [
			msg`stickers`,
			msg`emoji`,
			msg`packs`,
			msg`expressions`,
			msg`emoji packs`,
			msg`sticker packs`,
			msg`create emoji pack`,
			msg`create sticker pack`,
			msg`installed packs`,
			msg`created packs`,
		],
		description: msg`Manage expression packs`,
	},
	{
		id: 'applications-dev',
		tabType: 'applications',
		label: msg`Applications & Bots`,
		keywords: [msg`applications`, msg`bots`, msg`developer`, msg`api`, msg`oauth`, msg`create app`, msg`documentation`],
		description: msg`Create and manage applications and bots for your account`,
	},
	{
		id: 'applications-info',
		tabType: 'applications',
		label: msg`Application Information`,
		keywords: [
			msg`application name`,
			msg`public bot`,
			msg`redirect uri`,
			msg`oauth2 code grant`,
			msg`allowed redirects`,
		],
		description: msg`Edit application basics and redirect URIs`,
	},
	{
		id: 'applications-bot-profile',
		tabType: 'applications',
		label: msg`Bot Profile`,
		keywords: [msg`bot profile`, msg`bot avatar`, msg`bot username`, msg`bot bio`, msg`bot banner`, msg`friendly bot`],
		description: msg`Edit the bot avatar, tag, bio, banner, and friend request behavior`,
	},
	{
		id: 'applications-oauth-builder',
		tabType: 'applications',
		label: msg`OAuth2 URL Builder`,
		keywords: [
			msg`oauth2`,
			msg`url builder`,
			msg`authorize url`,
			msg`scopes`,
			msg`bot permissions`,
			msg`copy authorize url`,
		],
		description: msg`Build an authorization URL with scopes, redirects, and bot permissions`,
	},
	{
		id: 'applications-secrets',
		tabType: 'applications',
		label: msg`Secrets & Tokens`,
		keywords: [msg`client secret`, msg`bot token`, msg`regenerate`, msg`token`, msg`secret`],
		description: msg`View and regenerate client secrets and bot tokens`,
	},
	{
		id: 'chat-settings-auto-send-gifs',
		tabType: 'chat_settings',
		sectionId: 'media',
		label: msg`Automatically send GIFs when selected`,
		keywords: [
			msg`gif`,
			msg`gifs`,
			msg`auto send gif`,
			msg`auto send gifs`,
			msg`klipy`,
			msg`gif picker`,
			msg`gif behavior`,
			msg`send gif`,
		],
		description: msg`Automatically send GIFs from the picker without confirmation`,
	},
	{
		id: 'chat-settings-save-gif-favorites',
		tabType: 'chat_settings',
		sectionId: 'media',
		label: msg`Save GIF favorites as Saved Media`,
		keywords: [
			msg`gif`,
			msg`gifs`,
			msg`favorite gif`,
			msg`favorite gifs`,
			msg`gif favorites`,
			msg`saved media`,
			msg`star gif`,
			msg`gif behavior`,
			msg`gif saving`,
			msg`save gif`,
		],
		description: msg`Choose how starred GIF favorites are stored`,
	},
	{
		id: 'chat-settings-strip-tracking',
		tabType: 'chat_settings',
		sectionId: 'links',
		label: msg`Strip Tracking Parameters from URLs`,
		keywords: [
			msg`tracking`,
			msg`tracking parameters`,
			msg`utm`,
			msg`utm_source`,
			msg`fbclid`,
			msg`gclid`,
			msg`clean url`,
			msg`sanitize url`,
			msg`strip tracking`,
			msg`strip parameters`,
		],
		description: msg`Automatically remove tracking parameters from URLs in messages you send`,
	},
	{
		id: 'chat-settings-trust-domains',
		tabType: 'chat_settings',
		sectionId: 'links',
		label: msg`Trust all external links`,
		keywords: [
			msg`trust all`,
			msg`trust all links`,
			msg`trusted domains`,
			msg`trust domain`,
			msg`external link warning`,
			msg`link warnings`,
		],
		description: msg`Skip the external link warning for all domains`,
	},
	{
		id: 'chat-settings-reverse-image-search',
		tabType: 'chat_settings',
		sectionId: 'search_engines',
		label: msg`Reverse Image Search`,
		keywords: [
			msg`reverse image`,
			msg`reverse image search`,
			msg`image search`,
			msg`search by image`,
			msg`image lookup`,
			msg`saucenao`,
			msg`tineye`,
			msg`google lens`,
			msg`yandex images`,
		],
		description: msg`Configure reverse image search providers for images, avatars, banners, stickers, and emoji`,
	},
	{
		id: 'chat-settings-message-action-bar',
		tabType: 'chat_settings',
		sectionId: 'interaction',
		label: msg`Message Action Bar`,
		keywords: [
			msg`action bar`,
			msg`message action bar`,
			msg`message hover`,
			msg`hover buttons`,
			msg`quick reactions`,
			msg`shift expand`,
			msg`expand action bar`,
			msg`more button`,
			msg`message buttons`,
		],
		description: msg`Customize the action bar that appears when hovering over messages`,
	},
	{
		id: 'chat-settings-skip-mark-all-as-read-confirmation',
		tabType: 'chat_settings',
		sectionId: 'inbox',
		label: msg`Skip "Mark all as read" confirmation`,
		keywords: [
			msg`mark all as read`,
			msg`mark as read`,
			msg`inbox`,
			msg`confirmation`,
			msg`confirm`,
			msg`prompt`,
			msg`skip`,
		],
		description: msg`Mark all unread inbox channels as read immediately, without asking to confirm`,
	},
	{
		id: 'chat-settings-hide-muted-channels',
		tabType: 'chat_settings',
		sectionId: 'sidebar',
		label: msg`Hide muted channels by default`,
		keywords: [msg`muted`, msg`muted channels`, msg`hide muted`, msg`hide muted channels`, msg`new community muted`],
		description: msg`Hide channels you've muted from community sidebars`,
	},
	{
		id: 'accessibility-underline-links',
		tabType: 'accessibility',
		sectionId: 'visual',
		label: msg`Always underline links`,
		keywords: [msg`underline`, msg`underline links`, msg`link underline`, msg`hyperlink`, msg`link styling`],
		description: msg`Make links to websites stand out by always underlining them`,
	},
	{
		id: 'accessibility-text-selection',
		tabType: 'accessibility',
		sectionId: 'visual',
		label: msg`Enable text selection`,
		keywords: [msg`text selection`, msg`select text`, msg`copy text`, msg`selectable text`, msg`enable selection`],
		description: msg`Allow selecting text content in the app`,
	},
	{
		id: 'accessibility-dm-message-previews',
		tabType: 'accessibility',
		sectionId: 'visual',
		label: msg`DM Message Previews`,
		keywords: [
			msg`dm preview`,
			msg`dm previews`,
			msg`dm message preview`,
			msg`message preview`,
			msg`message previews`,
			msg`inbox preview`,
		],
		description: msg`Control when message previews are shown in the DM list`,
	},
	{
		id: 'accessibility-announce-new-messages',
		tabType: 'accessibility',
		sectionId: 'screen-reader',
		label: msg`Announce new messages`,
		keywords: [
			msg`announce new messages`,
			msg`screen reader announcements`,
			msg`read messages`,
			msg`live region`,
			msg`aria live`,
			msg`assistive technology`,
		],
		description: msg`Let screen readers announce new messages as they arrive in the open channel`,
	},
	{
		id: 'accessibility-escape-keyboard-mode',
		tabType: 'accessibility',
		sectionId: 'keyboard',
		label: msg`Escape key exits keyboard mode`,
		keywords: [msg`escape`, msg`escape key`, msg`esc`, msg`keyboard mode`, msg`exit keyboard mode`],
		description: msg`Allow pressing Escape to exit keyboard navigation mode`,
	},
	{
		id: 'accessibility-context-menu-shortcuts',
		tabType: 'accessibility',
		sectionId: 'keyboard',
		label: msg`Show context menu shortcuts`,
		keywords: [msg`context menu`, msg`context menu shortcuts`, msg`right click menu`, msg`menu shortcuts`],
		description: msg`Display keyboard shortcut indicators next to context menu items`,
	},
	{
		id: 'accessibility-textarea-focus-ring',
		tabType: 'accessibility',
		sectionId: 'keyboard',
		label: msg`Show focus ring on chat textarea`,
		keywords: [msg`focus ring`, msg`textarea`, msg`textarea focus ring`, msg`message input`, msg`composer focus`],
		description: msg`Display a visible focus indicator around the message input when focused`,
	},
	{
		id: 'accessibility-sync-reduced-motion',
		tabType: 'accessibility',
		sectionId: 'motion',
		label: msg`Sync reduced motion setting with system`,
		keywords: [
			msg`sync motion`,
			msg`sync reduced motion`,
			msg`system reduced motion`,
			msg`system motion`,
			msg`prefers reduced motion`,
		],
		description: msg`Automatically use your system's reduced motion preference`,
	},
	{
		id: 'notifications-unread-badge',
		tabType: 'notifications',
		sectionId: 'notifications',
		label: msg`Enable unread message badge`,
		keywords: [
			msg`unread`,
			msg`unread badge`,
			msg`unread message badge`,
			msg`app icon badge`,
			msg`message badge`,
			msg`dock badge`,
			msg`taskbar badge`,
		],
		description: msg`Show a red badge on the app icon when you have unread messages`,
	},
	{
		id: 'notifications-afk-timeout',
		tabType: 'notifications',
		sectionId: 'push',
		label: msg`Push notification inactive timeout`,
		keywords: [
			msg`afk`,
			msg`afk timeout`,
			msg`idle`,
			msg`idle timeout`,
			msg`away`,
			msg`inactive`,
			msg`inactive timeout`,
			msg`mobile push timeout`,
			msg`push timeout`,
			msg`desktop away`,
		],
		description: msg`Control how long you need to be inactive on desktop before mobile push notifications are sent`,
	},
	{
		id: 'notifications-master-volume',
		tabType: 'notifications',
		sectionId: 'sounds',
		label: msg`Master Volume`,
		keywords: [msg`volume`, msg`master volume`, msg`output volume`, msg`sound volume`, msg`notification volume`],
		description: msg`Adjust the master volume for all notification sounds`,
	},
	{
		id: 'linked-accounts-connections',
		tabType: 'linked_accounts',
		label: msg`Connections`,
		keywords: [
			msg`connections`,
			msg`linked accounts`,
			msg`linked`,
			msg`accounts`,
			msg`bluesky`,
			msg`domain`,
			msg`verify`,
			msg`verification`,
			msg`external`,
			msg`social`,
		],
		description: msg`Link external accounts and domains to your profile`,
		isVisible: shouldShowClaimedAccountSettings,
	},
];
function createSearchableItems(): Array<SearchableSettingItem> {
	const registryItems = getSearchableItemsFromRegistry(i18n);
	const additionalItems = ADDITIONAL_SEARCHABLE_ITEMS.filter(isSearchableDescriptorVisible).map((item) =>
		translateSearchableDescriptor(item, i18n),
	);
	return [...registryItems, ...additionalItems];
}
let cachedSearchableItems: Array<SearchableSettingItem> | null = null;
export function getSearchableItems(): Array<SearchableSettingItem> {
	if (!cachedSearchableItems) {
		cachedSearchableItems = createSearchableItems();
	}
	return cachedSearchableItems;
}
export function invalidateSearchCache(): void {
	cachedSearchableItems = null;
}
function normalizeSearchQuery(query: string): string {
	return query.toLowerCase().trim().replace(/\s+/g, ' ');
}
function getQueryWords(normalizedQuery: string): Array<string> {
	return normalizedQuery.split(' ').filter((word) => word.length > 0);
}
function calculateTextMatchScore(
	text: string,
	word: string,
	exactScore: number,
	prefixScore: number,
	containsScore: number,
): number {
	if (!text.includes(word)) {
		return 0;
	}
	if (text === word) {
		return exactScore;
	}
	return text.startsWith(word) ? prefixScore : containsScore;
}
function calculateKeywordMatchScore(keywords: Array<string>, word: string): number {
	return keywords.reduce((score, keyword) => score + calculateTextMatchScore(keyword, word, 80, 40, 20), 0);
}
function calculateMatchScore(item: SearchableSettingItem, queryWords: Array<string>): number {
	let score = 0;
	const labelLower = item.label.toLowerCase();
	const descriptionLower = item.description?.toLowerCase() ?? '';
	const keywordsLower = item.keywords.map((k) => k.toLowerCase());
	for (const word of queryWords) {
		score += calculateTextMatchScore(labelLower, word, 100, 50, 25);
		score += calculateKeywordMatchScore(keywordsLower, word);
		if (descriptionLower.includes(word)) {
			score += 10;
		}
	}
	return score;
}
function createTabsByType(tabs: Array<SettingsTab>): Map<UserSettingsTabType, SettingsTab> {
	const tabsByType = new Map<UserSettingsTabType, SettingsTab>();
	for (const tab of tabs) {
		if (!tabsByType.has(tab.type)) {
			tabsByType.set(tab.type, tab);
		}
	}
	return tabsByType;
}
function addMatchedItemToResults(
	resultsByTab: Map<UserSettingsTabType, SettingsSearchResult>,
	tab: SettingsTab,
	item: SearchableSettingItem,
	score: number,
): void {
	const existing = resultsByTab.get(item.tabType);
	if (existing) {
		existing.matchedItems.push(item);
		existing.score = Math.max(existing.score, score);
		return;
	}
	resultsByTab.set(item.tabType, {
		tab,
		matchedItems: [item],
		score,
	});
}
export function searchSettings(query: string, tabs: Array<SettingsTab>): Array<SettingsSearchResult> {
	const normalizedQuery = normalizeSearchQuery(query);
	if (!normalizedQuery) {
		return [];
	}
	const queryWords = getQueryWords(normalizedQuery);
	const items = getSearchableItems();
	const tabsByType = createTabsByType(tabs);
	const resultsByTab = new Map<UserSettingsTabType, SettingsSearchResult>();
	for (const item of items) {
		const tab = tabsByType.get(item.tabType);
		if (!tab) {
			continue;
		}
		const score = calculateMatchScore(item, queryWords);
		if (score > 0) {
			addMatchedItemToResults(resultsByTab, tab, item, score);
		}
	}
	const results = Array.from(resultsByTab.values());
	results.sort((a, b) => b.score - a.score);
	return results;
}
export function getMatchedSectionIds(results: Array<SettingsSearchResult>): Set<string> {
	const sectionIds = new Set<string>();
	for (const result of results) {
		for (const item of result.matchedItems) {
			if (item.sectionId) {
				sectionIds.add(item.sectionId);
			}
		}
	}
	return sectionIds;
}
export function getMatchedTabTypes(results: Array<SettingsSearchResult>): Set<UserSettingsTabType> {
	return new Set(results.map((r) => r.tab.type));
}
